import React, { useState } from 'react';
import styled from 'styled-components';
import { useResizeDetector } from 'react-resize-detector';
import type { Props as ReactResizeDetectorProps } from 'react-resize-detector/build/ResizeDetector';

import { HotelStars, ButtonTs } from '@lt/components';
// TODO Ошибка TS
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import HotelCardFacts from '@lt/components/components/HotelCardFacts/HotelCardFacts';
import HotelCardGallery from '@lt/components/components/HotelCard/components/HotelCardGallery';
import HotelCardRating from '@lt/components/components/HotelCard/components/HotelCardRating';
import HotelSearchResults from './HotelSearchResults';
import { EmbeddedCigarette } from './EmbeddedCigarette';
import { WidgetLogo } from '../../package/components';

import { useTrackShow, useGetHotelRooms, useRedirectToHotelPage } from '../hooks';

import { pluralizeOptions } from '../../../utils';
import { getCigaretteInitialParams, getStartDateByPreset } from '../utils';

import { useGetWhitelabelDomain } from '../../../context/constructorContext';

import {
  cigaretteValuesToSearchParams,
  SearchParamsWithNights,
} from '../../directionWidget/helpers';

import { SEARCH_TYPE_NAMES } from '../../../components/Cigarette/constants';
import { MIN_WIDTH, MIN_TWO_COLUMNS_CONTAINER_WIDTH, MIN_WIDE_CARD_WIDTH } from '../../constants';

import type { Values } from '../../../components/Cigarette/context';

const toursTitle = 'Туры в';
const hotelsTitle = 'Только проживание в';

const resizeDetectorConfig: ReactResizeDetectorProps = {
  handleHeight: false,
  handleWidth: true,
  refreshMode: 'throttle',
  refreshRate: 250,
  observerOptions: {
    box: 'border-box',
  },
};

interface IHotelCard {
  initialState: any;
}

const HotelCard = (props: IHotelCard): JSX.Element => {
  const { initialState } = props;
  const [widgetState, updateWidgetState] = useState({ ...initialState, hotelRooms: [] });

  const { hotelData, lang, redirect } = widgetState;
  const { departure } = widgetState.searchParams;

  const searchParams = {
    ...widgetState.searchParams,
    from_city: departure?.value || 'Any',
    from_country: departure?.iso2 || 'Any',
    to_city: hotelData.city.name_en,
    to_country: hotelData.country.iso2,
  };

  const cigaretteStartDate =
    searchParams.start_date instanceof Date
      ? searchParams.start_date
      : getStartDateByPreset(searchParams.start_date);

  const cigaretteInitialParams = getCigaretteInitialParams(
    hotelData,
    {
      ...searchParams,
      start_date: cigaretteStartDate,
    },
    departure
  );

  const partnerDomain = useGetWhitelabelDomain();
  const { redirectToHotelPage } = useRedirectToHotelPage({
    publicUrl: hotelData.public_url,
    redirect,
  });

  const { width = MIN_WIDTH, ref } = useResizeDetector(resizeDetectorConfig);
  const isMobile = width < MIN_TWO_COLUMNS_CONTAINER_WIDTH || false;
  const areSmallCards = width < MIN_WIDE_CARD_WIDTH || false;

  const [isInitial, setIsInitial] = useState(true);
  const [isSearchCompleted, setIsSearchCompleted] = useState(false);

  const displayedHotelRoomsCount = isMobile ? 3 : 4;
  const slidesPerView = !isMobile ? 3 : 1;

  useTrackShow({
    isInitial,
    isSearchCompleted,
    resultsSize: widgetState.hotelRooms?.length,
  });

  useGetHotelRooms({
    areSmallCards,
    isInitial,
    searchParams,
    updateWidgetState,
    setIsSearchCompleted,
  });

  const updateSearchParams = (values: Values) => {
    setIsInitial(false);

    const cigaretteSearchParams = cigaretteValuesToSearchParams(values);
    if (!cigaretteSearchParams) {
      return;
    }

    const { adults, kids, departure, nights, start_date } =
      cigaretteSearchParams as SearchParamsWithNights;
    const middleNights = Math.round((nights.min + nights.max) / 2);

    setIsSearchCompleted(false);
    updateWidgetState({
      ...widgetState,
      departure,
      searchParams: {
        ...searchParams,
        ...cigaretteSearchParams,
        adults,
        kids: kids?.length || 0,
        from_city: departure?.value || 'Any',
        nights: middleNights,
        start_date,
      },
    });
  };

  return (
    <StyledHotelCardContainer ref={ref}>
      <StyledHotelHeader>
        <div>
          <StyledHotelMetaTitle>
            <HotelStars value={hotelData.stars} size={12} />
            <StyledHotelRegion>
              {hotelData.city[`name_${lang}`]}, {hotelData.country[`name_${lang}`]}
            </StyledHotelRegion>
          </StyledHotelMetaTitle>
          <StyledHotelCardTitle onClick={() => redirectToHotelPage({ searchParams })}>
            {searchParams.search_type === SEARCH_TYPE_NAMES.PACKAGE ? toursTitle : hotelsTitle}{' '}
            <strong>{hotelData.name}</strong>
          </StyledHotelCardTitle>
        </div>
        <HotelCardFacts
          line={hotelData.features.line}
          beachDistance={hotelData.features.beach_distance}
          airportDistance={hotelData.features.airport_distance}
          beachSurface={hotelData.features.beach_surface}
          wifiType={hotelData.features.wi_fi}
        />
      </StyledHotelHeader>
      <StyledHotelGalleryWrapper>
        <StyledHotelRating className="" rating={hotelData.rating} />
        <StyledHotelCardGallery
          slidesPerView={slidesPerView}
          images={{
            list: hotelData.images,
            count: hotelData.images.length,
          }}
          autoplay={{ delay: 2000, disableOnInteraction: true }}
          bonusCount={null}
          currencyCode={widgetState.currency?.toUpperCase() || 'RUB'}
        />
      </StyledHotelGalleryWrapper>
      <EmbeddedCigarette
        containerWidth={width}
        hasMargins
        domain={partnerDomain}
        searchType={searchParams.search_type}
        initialValues={cigaretteInitialParams}
        onChange={updateSearchParams}
      />
      <HotelSearchResults
        hotelRoomsData={widgetState.hotelRooms}
        displayedHotelRoomsCount={displayedHotelRoomsCount}
        searchType={searchParams.search_type}
        redirectToHotelPage={(nightsCount) => redirectToHotelPage({ searchParams, nightsCount })}
        isMobile={isMobile}
        areSmallCards={areSmallCards}
        isInitial={!isSearchCompleted}
      />
      <StyledHotelCardFooter>
        {widgetState.hotelRooms.length - displayedHotelRoomsCount > 0 && (
          <StyledShowMoreButton onClick={() => redirectToHotelPage({ searchParams })}>
            Показать ещё {widgetState.hotelRooms.length - displayedHotelRoomsCount}{' '}
            {pluralizeOptions(widgetState.hotelRooms.length - displayedHotelRoomsCount)} размещения
          </StyledShowMoreButton>
        )}
        <WidgetLogo />
      </StyledHotelCardFooter>
    </StyledHotelCardContainer>
  );
};

const StyledHotelGalleryWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 160px;
  overflow: hidden;
`;

const StyledHotelCardGallery = styled(HotelCardGallery)`
  // повышаем specificity чтобы при многократном использовании компонентов,
  // в разных виджетах на одной странице,
  // первичные стили не перетирали переопределенные
  && {
    width: 100%;

    & .swiper-lt-preloader {
      display: none;
    }
  }
`;

const StyledHotelRating = styled(HotelCardRating)`
  // повышаем specificity чтобы при многократном использовании компонентов,
  // в разных виджетах на одной странице,
  // первичные стили не перетирали переопределенные
  && {
    position: absolute;
    right: 0;
    z-index: 10;
  }
`;

const StyledHotelCardContainer = styled.section`
  display: flex;
  flex-direction: column;
`;

const StyledHotelHeader = styled.div`
  padding: 8px;

  display: flex;
  flex-direction: column;

  @media (min-width: 944px) {
    flex-direction: row;
    justify-content: space-between;
  }
`;

const StyledHotelMetaTitle = styled.div`
  display: flex;
  margin-bottom: 4px;
`;

const StyledHotelRegion = styled.div`
  font-size: 11px;
  line-height: 14px;
`;

const StyledHotelCardTitle = styled.a`
  // повышаем specificity чтобы при многократном использовании компонентов,
  // в разных виджетах на одной странице,
  // первичные стили не перетирали переопределенные
  && {
    color: #17181a !important;
  }

  font-size: 18px;
  font-weight: 400;
  line-height: 22px;

  text-decoration: none;
  cursor: pointer;
`;

const StyledHotelCardFooter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  margin: 8px 16px 16px 16px;
`;

const StyledShowMoreButton = styled(ButtonTs)`
  // повышаем specificity чтобы при многократном использовании компонентов,
  // в разных виджетах на одной странице,
  // первичные стили не перетирали переопределенные
  && {
    background: inherit;

    width: fit-content;
    padding: 12px 18px;
    margin-bottom: 12px;
  }
`;

export default HotelCard;
